import {
  CMS_PAGE_DISPLAYED_EVENT,
  getLoggedInStatus,
  getPreviousSiteSection,
  SEARCH_RESULTS,
  trackEvent,
} from '@seek/cmsu-analytics';
import {
  Stage,
  type Article,
  type GetSectionsQuery,
  type SectionFooter,
  type SectionHeader,
} from '@seek/cmsu-cms-connect';
import { SearchBox } from '@seek/cmsu-components';
import { ITEM_DISPLAY_TYPE } from '@seek/cmsu-components/src/helpers/articleHelper';
import { ArticleItem } from '@seek/cmsu-components/src/modules/ArticleItem/ArticleItem';
import { resizeImageFromHygraph } from '@seek/cmsu-components/src/utils/imageHelper';
import {
  getSearchPathBy,
  isAboutSectionSearch,
} from '@seek/cmsu-components/src/utils/searchHelper';
import { useMelwaysLink } from '@seek/melways-react';
import type { Brand, Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ContentBlock,
  Heading,
  Loader,
  Pagination,
  Stack,
} from 'braid-design-system';
import { useEffect, useState } from 'react';
import { useLoaderData, useNavigate, useParams } from 'react-router';

import { convertMelwaysLocale } from '../../helpers/convertMelwaysLocale';
import translations from '../.vocab';
import { PageLayout } from '../components/PageLayout/PageLayout';
import { useScreenSize } from '../hooks/useScreenSize';
import {
  PAGE_LIMIT,
  useAuth,
  useConfig,
  useHandleSearch,
  useHandleSearchResultsOnMount,
} from '../shared/hooks';
import { useFetchSuggestions } from '../shared/hooks/useFetchSuggestions';
import type { SearchResultsResponse } from '../shared/hooks/useSearchResultHooks';

import { NoSearchResult } from './pageComponents/NoSearchResult';

type SearchData = {
  id: string;
  title: string;
  slug: string;
  categoryName: string;
  sectionName: string;
  parentSectionName: string;
  publishedAt: string;
  featureImage: {
    width: number;
    height: number;
    url: string;
    alt: string;
  };
};

const getParentSection = (searchResultsData: SearchData) => {
  if (!searchResultsData.parentSectionName) return;
  return {
    parentSection: {
      sectionName: searchResultsData.parentSectionName,
    },
  };
};

const mapSearchResultsToArticle = (searchResultsData: SearchData): Article =>
  // Map the search results to Article type
  ({
    id: searchResultsData.id,
    title: searchResultsData.title,
    slug: searchResultsData.slug,
    category: {
      name: searchResultsData.categoryName,
      relatedCategorySection: {
        sectionName: searchResultsData.sectionName,
        ...getParentSection(searchResultsData),
      },
    },
    publishDate: searchResultsData.publishedAt,
    featureImage: [
      {
        ...(searchResultsData.featureImage?.url && {
          url: resizeImageFromHygraph({
            imageUrl: searchResultsData.featureImage.url,
            width: 480,
            height: 320,
          }),
        }),
        ...(searchResultsData.featureImage?.alt && {
          alt: searchResultsData.featureImage.alt,
        }),
        ...(searchResultsData.featureImage?.width && {
          width: searchResultsData.featureImage.width,
        }),
        ...(searchResultsData.featureImage?.height && {
          height: searchResultsData.featureImage.height,
        }),
      },
    ],
    stage: Stage.Published,
  }) as Article;

export const SearchResults = () => {
  const { term, pageNumber } = useParams();
  const { authenticationStatus } = useAuth();
  const decodedTerm = decodeURIComponent(term || '');
  const navigate = useNavigate();
  const { t } = useTranslations(translations);
  const { melwaysLocale, language, section, brand } = useConfig();
  const locale = convertMelwaysLocale(melwaysLocale);
  const [searchInput, setSearchInput] = useState<string>('');
  const urlResolver = useMelwaysLink();
  const fetchedSuggestions = useFetchSuggestions(locale, searchInput);
  const {
    handleSearch,
    searchedTerm,
    isSearchInProgress,
    searchResults,
    setSearchedTerm,
  } = useHandleSearch();
  const searchResultsTotal = searchResults?.total;
  useHandleSearchResultsOnMount({
    handleSearch,
  });

  const isAboutSection = isAboutSectionSearch(section);

  useEffect(() => {
    const { isPending, isLoggedIn } = getLoggedInStatus(authenticationStatus);

    if (!isPending && searchResultsTotal !== undefined) {
      trackEvent(CMS_PAGE_DISPLAYED_EVENT, {
        currentPage: SEARCH_RESULTS,
        siteSection: getPreviousSiteSection() || SEARCH_RESULTS,
        searchKeyword: decodedTerm,
        searchResultsTotal,
        pageNumber,
        isLoggedIn,
      });
    }
  }, [
    handleSearch,
    pageNumber,
    decodedTerm,
    searchResultsTotal,
    authenticationStatus,
  ]);

  const data = useLoaderData() as GetSectionsQuery;
  const hygraphSection = data?.sections[0];

  const numberOfResultsPages =
    (searchResults && Math.ceil(searchResults.total / PAGE_LIMIT)) || 1;

  const getSearchResultHeading = () => {
    if (isSearchInProgress) {
      return `${t('Search in progress')} \u2018${searchedTerm}\u2019`;
    }

    const headingString = `${t(
      'Search Results for',
    )} \u2018${searchedTerm}\u2019`;

    return `${
      searchResults && searchResults.total > 0 ? searchResults.total : t('No')
    }
     ${headingString}`;
  };

  return (
    <PageLayout
      layoutProps={{
        title: t('Search results'),
        section: {
          sectionName: hygraphSection?.sectionName,
          theme:
            hygraphSection?.theme === null ? undefined : hygraphSection?.theme,
          headerOptions:
            hygraphSection?.headerOptions === null
              ? undefined
              : (hygraphSection?.headerOptions as SectionHeader),
          footerOptions:
            hygraphSection?.footerOptions === null
              ? undefined
              : (hygraphSection?.footerOptions as SectionFooter),
        },
      }}
    >
      <Box
        paddingY="xlarge"
        paddingX={{
          mobile: 'medium',
          desktop: 'none',
        }}
        background="surface"
      >
        <Box paddingY="small">
          <ContentBlock>
            <Stack space="large">
              <Box flexDirection="row" display="flex" paddingBottom="large">
                <Heading level="2">{getSearchResultHeading()}</Heading>
              </Box>
            </Stack>

            <Box paddingBottom="medium">
              <SearchBox
                searchTerm={searchedTerm}
                placeholderText={
                  isAboutSection
                    ? t('Search box news suggestion')
                    : t('Search box suggestion')
                }
                searchButtonText={t('Search button text')}
                section={section}
                locale={locale}
                onSearch={(s) => {
                  navigate(
                    urlResolver({
                      language,
                      brand,
                      path: `/${getSearchPathBy(section)}/${encodeURIComponent(
                        s,
                      )}/1`,
                    }),
                  );
                  if (s !== searchedTerm) {
                    setSearchedTerm(s);
                  } else {
                    handleSearch(s);
                  }
                }}
                minChars={3}
                minCharsErrorMessage={t('Minimum chars error message', {
                  minChars: '3',
                })}
                onKeyPress={setSearchInput}
                onSelect={(path) => {
                  // For some reason using useNavigate is not forcing SSR loader to work for Articles when being selected.
                  // We are using window.location.href to force SSR loader to work and route outside of react router.
                  // The apollo client is not making a full network request and returning the data from the cache and it is empty.
                  window.location.href = `${window.location.origin}${path}`;
                }}
                titleSuggestions={fetchedSuggestions}
              />
            </Box>
            {isSearchInProgress && (
              <Box paddingBottom="medium">
                <Loader delayVisibility={true} size="large" />
              </Box>
            )}
            {searchResults && !isSearchInProgress && (
              <DisplayArticleItem
                searchResults={searchResults}
                brand={brand}
                language={language}
              />
            )}
            {!isSearchInProgress &&
              searchResults &&
              searchResults.total > 0 &&
              numberOfResultsPages > 1 && (
                <Box
                  paddingY="xlarge"
                  paddingX={{ mobile: 'small', desktop: 'xlarge' }}
                >
                  <Pagination
                    page={Number(pageNumber ?? 1)}
                    total={numberOfResultsPages}
                    label="pagination"
                    linkProps={({ page: pageInPagination }) => ({
                      href: `/${getSearchPathBy(
                        section,
                      )}/${searchedTerm}/${pageInPagination}`,
                    })}
                  />
                </Box>
              )}

            {!isSearchInProgress && !searchResults && (
              <NoSearchResult searchedTerm={searchedTerm} />
            )}
          </ContentBlock>
        </Box>
      </Box>
    </PageLayout>
  );
};

const DisplayArticleItem = ({
  searchResults,
  brand,
  language,
}: {
  searchResults: SearchResultsResponse;
  brand: Brand;
  language: Language;
}) => {
  const screenSize = useScreenSize();

  return (
    <Box paddingTop={{ mobile: 'large', desktop: 'xlarge' }}>
      <Stack space="gutter">
        {searchResults.data.map((data, index) => (
          <ArticleItem
            displayType={
              screenSize.isMobile
                ? ITEM_DISPLAY_TYPE.CARD
                : ITEM_DISPLAY_TYPE.LIST_ITEM
            }
            article={mapSearchResultsToArticle(data as SearchData)}
            brand={brand}
            language={language}
            key={data.id}
            isLastItem={index === searchResults.data.length - 1}
            fieldVisibility={{
              showCategory: true,
              showPublishDate: true,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};
